import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { SignalStringFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level multi-line text input for signal forms — the textarea sibling of
 * `TextFieldComponent`, sharing its base and therefore its whole store and
 * validation contract:
 *
 * ```html
 * <pace-core-multiline-text-field [formField]="signalForm.reportText" rows="4" />
 * ```
 *
 * Line-count and length rules belong in the owning form's schema, same as
 * everywhere else — this component only renders what it is handed.
 *
 * Not carried over from the reactive-forms `TextInputComponent`: the
 * `formatMultilineTextPattern` reflow and its format-on-blur behaviour, which
 * rewrite the value through `MultilineTextModel.getFormattedText()`/`trimLines()`.
 * Those live on the model class rather than the component, so they need a home in
 * the new architecture — most likely a transform on the way into the slice —
 * before this can replace the report-text and trouble-text fields.
 */
@Component({
    selector: 'pace-multiline-text-field',
    templateUrl: './multiline-text-field.component.html',
    styleUrls: ['./multiline-text-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultilineTextFieldComponent extends SignalStringFieldBaseDirective {
    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    public readonly minLength = input<number | undefined>(undefined);
    public readonly maxLength = input<number | undefined>(undefined);
    public readonly pattern = input<readonly RegExp[]>([]);

    // --- own inputs ----------------------------------------------------------

    public readonly placeholder = input('');
    public readonly rows = input(3);
    public readonly cols = input<number | undefined>(undefined);

    /**
     * The schema's first pattern, as the native `pattern` attribute expects it.
     * A textarea has no native `pattern` support, so this is exposed for assistive
     * tech only — the schema rule is what actually reports.
     */
    public readonly patternAttribute = computed(() => this.pattern()[0]?.source);
}
